/**
 * Vida no jardim: patos no lago, pássaros na relva que fogem de quem passa, e a
 * luz de fora a seguir a hora real (dia, fim de tarde, noite, madrugada).
 *
 * Tudo decorativo: nada disto entra na colisão nem vai à rede — cada cliente
 * tem os seus patos e os seus pássaros. As contas estão em funções puras,
 * testadas; a classe só as passa para objectos do Phaser.
 */
import {
  BUILDING,
  inLake,
  isOutside,
  isUpstairs,
  isWalkable,
  onGardenPath,
  PARK_ROW,
  PROPS,
  ROOMS,
  TILE,
  UPSTAIRS_OFFSET_ROWS,
} from "@greenvoice/shared";
import type PhaserT from "phaser";

// ── A luz do dia ───────────────────────────────────────────────────────────

export interface Luz {
  /** Cor do véu por cima do jardim. */
  cor: number;
  /** Quanto o véu tapa (0 = dia claro). */
  alfa: number;
  /** 0 de dia, 1 de noite — acende os candeeiros. */
  noite: number;
}

const NOITE = 0x0b1633;
const POENTE = 0xff8a3d;
const ALFA_NOITE = 0.5;
const ALFA_POENTE = 0.14;

const entre = (h: number, a: number, b: number) => Math.min(1, Math.max(0, (h - a) / (b - a)));

function misturar(a: number, b: number, t: number): number {
  const c = (x: number, s: number) => (x >> s) & 0xff;
  const m = (s: number) => Math.round(c(a, s) + (c(b, s) - c(a, s)) * t) << s;
  return m(16) | m(8) | m(0);
}

/**
 * A luz de fora para uma hora do dia (0-24, com fracções).
 *
 * Dia claro das 8h às 17h30; o sol põe-se entre as 17h30 e as 19h (laranja),
 * escurece até às 20h30 (azul); noite até às 5h30; e amanhece até às 8h.
 */
export function luzDoDia(hora: number): Luz {
  const h = ((hora % 24) + 24) % 24;
  if (h >= 8 && h < 17.5) return { cor: NOITE, alfa: 0, noite: 0 };
  if (h >= 17.5 && h < 19) {
    const t = entre(h, 17.5, 19);
    return { cor: POENTE, alfa: ALFA_POENTE * t, noite: 0 };
  }
  if (h >= 19 && h < 20.5) {
    const t = entre(h, 19, 20.5);
    return { cor: misturar(POENTE, NOITE, t), alfa: ALFA_POENTE + (ALFA_NOITE - ALFA_POENTE) * t, noite: t };
  }
  if (h >= 5.5 && h < 8) {
    const t = entre(h, 5.5, 8); // 0 = ainda noite, 1 = dia
    return {
      cor: misturar(NOITE, POENTE, Math.min(1, t * 1.6)),
      alfa: ALFA_NOITE * (1 - t),
      noite: Math.max(0, 1 - t * 2),
    };
  }
  return { cor: NOITE, alfa: ALFA_NOITE, noite: 1 };
}

export const horaDe = (d: Date): number => d.getHours() + d.getMinutes() / 60;

// ── Os patos ───────────────────────────────────────────────────────────────

/**
 * Os percursos dos patos, em tiles (o pé do pato), tirados do lago DESENHADO e
 * não da elipse `LAKE` (que é só a colisão): junto à margem há pedras, juncos e
 * nenúfares, e a casa flutuante ocupa o meio de cima (cols 14-20, até à linha
 * 30). Mediu-se, numa captura, a distância de cada ponto de água à coisa seca
 * mais próxima, e a água livre (≥ 0,6 tile de folga) são três bolsas: à
 * esquerda da casa, por baixo dela, e à direita (o pior ponto dos percursos
 * ficou a 0,5 tile de tudo o que é seco). O nenúfar da direita fecha a
 * passagem entre as duas últimas.
 *
 * Daí: dois patos vão e voltam da bolsa da esquerda à de baixo (nas colunas da
 * casa sempre abaixo da linha 30,6, à frente dela), e o terceiro anda às voltas
 * na bolsa da direita.
 */
export const PERCURSO_LARGO: readonly { x: number; y: number }[] = [
  { x: 12.9, y: 28.9 },
  { x: 12.6, y: 29.8 },
  { x: 13.6, y: 30.25 },
  { x: 14.2, y: 31.05 },
  { x: 16.3, y: 31.2 },
  { x: 17.7, y: 31.0 },
];

export const PATOS = [
  { percurso: PERCURSO_LARGO, fase: 0, periodo: 44000 },
  { percurso: PERCURSO_LARGO, fase: 0.35, periodo: 52000 },
  { percurso: "volta", fase: 0.4, periodo: 20000 },
] as const;

/** A volta do terceiro pato, na bolsa da direita: centro e raios, em tiles. */
export const VOLTA = { cx: 20.9, cy: 29.7, rx: 0.45, ry: 0.3 } as const;

/** O ponto a uma fracção `k` (0-1) do comprimento de uma linha partida. */
function aoLongo(linha: readonly { x: number; y: number }[], k: number): { x: number; y: number } {
  const trocos = linha.slice(1).map((p, n) => Math.hypot(p.x - linha[n]!.x, p.y - linha[n]!.y));
  let falta = Math.min(1, Math.max(0, k)) * trocos.reduce((a, b) => a + b, 0);
  for (let n = 0; n < trocos.length; n += 1) {
    const d = trocos[n]!;
    if (falta <= d || n === trocos.length - 1) {
      const u = d === 0 ? 0 : Math.min(1, falta / d);
      const a = linha[n]!;
      const b = linha[n + 1]!;
      return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
    }
    falta -= d;
  }
  return linha[0]!;
}

/** Onde está o pato (px) no instante `t` (ms), e para que lado olha. */
export function posicaoDoPato(pato: (typeof PATOS)[number], t: number): { x: number; y: number; paraDireita: boolean } {
  const ciclo = (((pato.fase + t / pato.periodo) % 1) + 1) % 1;
  if (pato.percurso === "volta") {
    const a = ciclo * Math.PI * 2;
    return {
      x: (VOLTA.cx + Math.cos(a) * VOLTA.rx) * TILE,
      y: (VOLTA.cy + Math.sin(a) * VOLTA.ry) * TILE,
      // A derivada de cos é −sen: a volta é no sentido dos ponteiros (y para baixo).
      paraDireita: -Math.sin(a) >= 0,
    };
  }
  // Vai e volta: na primeira metade do ciclo segue a linha, na segunda regressa.
  const ida = ciclo < 0.5;
  const k = ida ? ciclo * 2 : 2 - ciclo * 2;
  const p = aoLongo(pato.percurso, k);
  // Olha para onde vai AGORA, não para a ponta do percurso: a linha tem curvas.
  const aFrente = aoLongo(pato.percurso, k + (ida ? 0.002 : -0.002));
  return { x: p.x * TILE, y: p.y * TILE, paraDireita: aFrente.x >= p.x };
}

// ── Os pássaros ────────────────────────────────────────────────────────────

/** Tiles de relva onde um pássaro pode pousar: jardim, fora do lago, do caminho e do parque. */
export const RELVA: readonly { col: number; row: number }[] = (() => {
  const out: { col: number; row: number }[] = [];
  for (let row = 0; row < PARK_ROW; row += 1) {
    for (let col = 1; col < 43; col += 1) {
      if (!isOutside(col, row) || inLake(col, row) || onGardenPath(col, row)) continue;
      if (!isWalkable((col + 0.5) * TILE, (row + 0.5) * TILE)) continue;
      out.push({ col, row });
    }
  }
  return out;
})();

/** Uma pessoa a menos disto assusta o pássaro. */
export const SUSTO_PX = 70;

export function assustado(passaro: { x: number; y: number }, pessoas: readonly { x: number; y: number }[]): boolean {
  return pessoas.some((p) => Math.hypot(p.x - passaro.x, p.y - passaro.y) < SUSTO_PX);
}

/** Posição num voo de `de` a `para`, com um arco para cima, para `k` de 0 a 1. */
export function noVoo(
  de: { x: number; y: number },
  para: { x: number; y: number },
  k: number,
): { x: number; y: number } {
  const t = Math.min(1, Math.max(0, k));
  const arco = Math.sin(t * Math.PI) * Math.min(90, 30 + Math.hypot(para.x - de.x, para.y - de.y) * 0.25);
  return { x: de.x + (para.x - de.x) * t, y: de.y + (para.y - de.y) * t - arco };
}

// ── A arte (pixel a pixel, blocos de 2 px como o resto do mundo) ────────────

const CORES: Record<string, string> = {
  G: "#2f7d4a", // cabeça do pato
  g: "#3f9a5c",
  k: "#101418",
  Y: "#f2b632", // bico
  W: "#f4f1ea",
  B: "#8a6a4a", // corpo do pato
  b: "#b89770",
  s: "#6d4f35", // pássaro
  S: "#9c7a55",
  c: "#e9dcc7",
};

const PATO = ["......GG..", ".....GgkG.", ".....GGGYY", ".W...WBB..", "WBBBBBBBB.", ".BbbbbbBB.", "..BBBBBB.."];

const PASSARO_POUSADO = ["..ss..", ".sSkY.", "SSSs..", ".Scc..", "..k..."];
const PASSARO_VOO_A = ["S....S", ".SssS.", "..sskY", "......"];
const PASSARO_VOO_B = ["......", ".SssS.", "S.sskY", "S....S"];

/** Tamanho de cada "pixel" da arte: o pato em blocos de 3 para se ler ao pé do boneco, os pássaros em 2. */
function textura(scene: PhaserT.Scene, chave: string, mapa: readonly string[], PX = 2): void {
  if (scene.textures.exists(chave)) return;
  const w = Math.max(...mapa.map((l) => l.length));
  const canvas = document.createElement("canvas");
  canvas.width = w * PX;
  canvas.height = mapa.length * PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  mapa.forEach((linha, y) => {
    [...linha].forEach((ch, x) => {
      const cor = CORES[ch];
      if (!cor) return;
      ctx.fillStyle = cor;
      ctx.fillRect(x * PX, y * PX, PX, PX);
    });
  });
  scene.textures.addCanvas(chave, canvas);
}

const T_PATO = "vida:pato";
const T_POUSADO = "vida:passaro";
const T_VOO_A = "vida:passaro:voo-a";
const T_VOO_B = "vida:passaro:voo-b";

/**
 * O que é "fora": à superfície, o mapa todo menos o prédio; no 1.º andar, o
 * telhado (o prédio menos o bloco das salas de cima). O jardim que se vê lá de
 * cima é o de baixo, já coberto — cobri-lo outra vez escurecia-o a dobrar.
 */
export function rectangulosDeFora(): [number, number, number, number][] {
  const W = 44 * TILE;
  const alturaSuperficie = 52 * TILE;
  const b = {
    x: BUILDING.col * TILE,
    y: BUILDING.row * TILE,
    w: BUILDING.w * TILE,
    h: BUILDING.h * TILE,
  };
  const out: [number, number, number, number][] = [
    [0, 0, W, b.y],
    [0, b.y, b.x, b.h],
    [b.x + b.w, b.y, W - b.x - b.w, b.h],
    [0, b.y + b.h, W, alturaSuperficie - b.y - b.h],
  ];
  const cima = ROOMS.filter((r) => isUpstairs(r.row));
  const topo = (BUILDING.row + UPSTAIRS_OFFSET_ROWS) * TILE;
  if (cima.length === 0) {
    out.push([b.x, topo, b.w, b.h]);
    return out;
  }
  // O bloco das salas de cima, com a parede à volta.
  const c0 = (Math.min(...cima.map((r) => r.col)) - 1) * TILE;
  const c1 = (Math.max(...cima.map((r) => r.col + r.w)) + 1) * TILE;
  const r0 = (Math.min(...cima.map((r) => r.row)) - 1) * TILE;
  const r1 = (Math.max(...cima.map((r) => r.row + r.h)) + 1) * TILE;
  out.push(
    [b.x, topo, b.w, r0 - topo],
    [b.x, r0, c0 - b.x, r1 - r0],
    [c1, r0, b.x + b.w - c1, r1 - r0],
    [b.x, r1, b.w, topo + b.h - r1],
  );
  return out;
}

// ── A peça no mundo ────────────────────────────────────────────────────────

const N_PASSAROS = 8;
/** Por cima de tudo o que há no mundo — o véu da noite tapa até o helicóptero. */
const PROFUNDIDADE_VEU = 2_000_000;
const VOO_MS = 1100;

interface Passaro {
  img: PhaserT.GameObjects.Image;
  x: number;
  y: number;
  voo: { de: { x: number; y: number }; para: { x: number; y: number }; desde: number } | null;
  proximoSalto: number;
}

const porCena = new WeakMap<PhaserT.Scene, VidaDoJardim>();

export function vidaDaCena(scene: PhaserT.Scene): VidaDoJardim | undefined {
  return porCena.get(scene);
}

export class VidaDoJardim {
  private readonly patos: {
    img: PhaserT.GameObjects.Image;
    onda: PhaserT.GameObjects.Ellipse;
    fundo: PhaserT.GameObjects.Ellipse;
    /** Linha de água, em px da imagem a contar de cima (o que fica abaixo afunda). */
    linha: number;
  }[];
  private readonly passaros: Passaro[];
  private readonly veus: PhaserT.GameObjects.Rectangle[];
  private readonly candeeiros: PhaserT.GameObjects.Image[];
  private ultimaLuz = -Infinity;

  /**
   * `pato` é a chave do PNG do pato (virado para a direita); se não carregou,
   * fica o pato desenhado aqui. Para nadar para a esquerda, espelha-se.
   */
  constructor(scene: PhaserT.Scene, brilho: string, aditivo: PhaserT.BlendModes | number, pato?: string) {
    textura(scene, T_PATO, PATO, 3);
    const texturaDoPato = pato && scene.textures.exists(pato) ? pato : T_PATO;
    textura(scene, T_POUSADO, PASSARO_POUSADO);
    textura(scene, T_VOO_A, PASSARO_VOO_A);
    textura(scene, T_VOO_B, PASSARO_VOO_B);

    this.patos = PATOS.map(() => {
      const img = scene.add.image(0, 0, texturaDoPato);
      // O PNG traz o pato inteiro, patas incluídas: a boiar, as patas e o fundo
      // da barriga estão debaixo de água. Corta-se a imagem nessa linha (72% da
      // altura) e é esse corte que assenta no ponto do pato. O pato desenhado
      // aqui já é só o que fica fora de água.
      const linha = texturaDoPato === T_PATO ? img.height : Math.round(img.height * 0.72);
      img.setOrigin(0.5, linha / img.height);
      return {
        img,
        linha,
        fundo: scene.add.ellipse(0, 0, 28, 6, 0x0b3a5c, 0.22),
        onda: scene.add.ellipse(0, 0, 34, 5, 0xffffff, 0.35),
      };
    });

    this.passaros = Array.from({ length: N_PASSAROS }, (_, i) => {
      const { x, y } = this.sitioAoCalhas(i * 7919);
      return {
        img: scene.add.image(x, y, T_POUSADO).setOrigin(0.5, 1).setDepth(y),
        x,
        y,
        voo: null,
        proximoSalto: 2000 + Math.random() * 6000,
      };
    });

    this.veus = rectangulosDeFora().map(([x, y, w, h]) =>
      scene.add.rectangle(x, y, w, h, NOITE, 0).setOrigin(0, 0).setDepth(PROFUNDIDADE_VEU),
    );
    // À noite os candeeiros do parque acendem-se a sério: um brilho por cima do véu.
    this.candeeiros = PROPS.filter((p) => p.kind === "streetLamp").map((p) => {
      const img = scene.add
        .image((p.col + 0.5) * TILE, (p.row + 1) * TILE - TILE * 1.2, brilho)
        .setDepth(PROFUNDIDADE_VEU + 1)
        .setBlendMode(aditivo)
        .setAlpha(0);
      img.setDisplaySize(TILE * 4.5, TILE * 5);
      return img;
    });
    porCena.set(scene, this);
  }

  /** Um frame. `pessoas` são os pés de toda a gente à vista (px). */
  update(agora: number, pessoas: readonly { x: number; y: number }[], data: Date = new Date()): void {
    // Os patos.
    PATOS.forEach((pato, i) => {
      const p = posicaoDoPato(pato, Date.now());
      const boia = Math.sin(agora / 420 + i * 2) * 0.8;
      const { img, onda, fundo, linha } = this.patos[i]!;
      // O pato sobe e desce; a água não. O corte acompanha a água, por isso ao
      // descer afunda-se um pouco mais e ao subir mostra um pouco mais de corpo.
      img
        .setPosition(p.x, p.y + boia)
        .setCrop(0, 0, img.width, Math.max(1, linha - boia))
        .setFlipX(!p.paraDireita)
        .setDepth(p.y);
      fundo.setPosition(p.x, p.y + 3).setDepth(p.y - 1);
      onda.setPosition(p.x, p.y).setDepth(p.y + 1);
      onda.setScale(1 + 0.1 * Math.sin(agora / 300 + i), 1);
    });

    // Os pássaros.
    this.passaros.forEach((b, i) => {
      if (b.voo) {
        const k = (agora - b.voo.desde) / VOO_MS;
        const p = noVoo(b.voo.de, b.voo.para, k);
        b.img.setPosition(p.x, p.y).setDepth(PROFUNDIDADE_VEU - 1);
        b.img.setTexture(Math.floor(agora / 90) % 2 === 0 ? T_VOO_A : T_VOO_B);
        if (k >= 1) {
          b.x = b.voo.para.x;
          b.y = b.voo.para.y;
          b.voo = null;
          b.img.setTexture(T_POUSADO).setPosition(b.x, b.y).setDepth(b.y);
          b.proximoSalto = agora + 1500 + Math.random() * 5000;
        }
        return;
      }
      if (assustado(b, pessoas)) {
        const para = this.sitioAoCalhas(Math.floor(agora) + i * 131, pessoas);
        b.voo = { de: { x: b.x, y: b.y }, para, desde: agora };
        b.img.setFlipX(para.x < b.x);
        return;
      }
      if (agora >= b.proximoSalto) {
        // Um saltinho ao lado, ou virar-se — e a bicar entre saltos.
        const dx = (Math.random() - 0.5) * 16;
        const dy = (Math.random() - 0.5) * 8;
        const col = Math.floor((b.x + dx) / TILE);
        const row = Math.floor((b.y + dy) / TILE);
        if (RELVA.some((r) => r.col === col && r.row === row)) {
          b.x += dx;
          b.y += dy;
          b.img.setFlipX(dx < 0);
        }
        b.proximoSalto = agora + 1200 + Math.random() * 4000;
      }
      const bica = Math.sin(agora / 180 + i * 3) > 0.92 ? 1 : 0;
      b.img.setPosition(b.x, b.y + bica).setDepth(b.y);
    });

    // A luz, uma vez por minuto chega.
    if (agora - this.ultimaLuz > 60000) {
      this.ultimaLuz = agora;
      this.aplicarLuz(luzDoDia(horaDe(data)));
    }
  }

  /** Força a luz de uma hora (para ver a noite sem esperar por ela). */
  aplicarLuz(luz: Luz): void {
    for (const v of this.veus) v.setFillStyle(luz.cor, luz.alfa);
    for (const c of this.candeeiros) c.setAlpha(0.55 * luz.noite);
  }

  private sitioAoCalhas(semente: number, longeDe: readonly { x: number; y: number }[] = []): { x: number; y: number } {
    for (let tentativa = 0; tentativa < 20; tentativa += 1) {
      const r = RELVA[Math.abs(semente * 31 + tentativa * 977 + Math.floor(Math.random() * 1e6)) % RELVA.length]!;
      const p = { x: (r.col + 0.2 + Math.random() * 0.6) * TILE, y: (r.row + 0.3 + Math.random() * 0.6) * TILE };
      if (!assustado(p, longeDe)) return p;
    }
    const r = RELVA[0]!;
    return { x: (r.col + 0.5) * TILE, y: (r.row + 0.5) * TILE };
  }
}
